// @ts-nocheck
import _ from "lodash";
import { CONTRACTOR_TYPE_3RD_PARTY } from "../../constants/nmaes";
import { getCssVariableHandler } from "../../styles/getCssVariables";

/**
 * check if a string is url or not
 */
export const isURL = (url) => {
  try {
    new URL(url);
    return true;
  } catch (_) {
    return false;
  }
};

/**
 * check if the param is a file or not
 */
export const isThisAFile = (thing) => {
  return thing instanceof File || thing instanceof Blob;
};

/**
 * Converts frontend annotation data to the backend format.
 *
 * @param {Array} frontendData - Array of annotation objects from the frontend.
 * Each object should include:
 * - id: string (unique identifier)
 * - type: "Text" | "Signature"
 * - value: string (text content or signature file URL)
 * - x, y, width, height: number (position and size)
 * - page: number (page number)
 *
 * @returns {Array} Array of backend-formatted annotation objects.
 * For Text:
 *   - id, text, x, y, page, width, height
 * For Signature:
 *   - id, signatureId (same as id), signatureFileUrl, x, y, page, width, height
 */
export function convertFrontendToBackend(frontendData) {
  return frontendData
    .filter((field) => field.type === "Text" || field.type === "Signature")
    .map((field) => {
      const base = {
        ...field,
        id: field.id,
        x: field.x,
        y: field.y,
        page: field.page,
        width: field.width,
        height: field.height,
      };

      if (field.type === "Text") {
        return {
          ...base,
          text: field.value,
          elementId: field.id,
          textId: field?._id,
        };
      }

      if (field.type === "Signature") {
        return {
          ...base,
          signatureFileUrl: field.value,
          elementId: field.id,
          signatureId: field?._id,
        };
      }

      return null;
    })
    .filter(Boolean);
}

/**
 * Converts backend annotation data to the frontend format.
 *
 * @param {Array} backendData - Array of annotation objects from the backend.
 * Each object can include:
 * - id: string
 * - text: string (for Text)
 * - signatureFileUrl: string (for Signature)
 * - signatureId: string (used as id in frontend Signature)
 * - x, y, width, height: number
 * - page: number
 *
 * @returns {Array} Array of frontend-formatted annotation objects.
 * Each object will include:
 * - id: string
 * - type: "Text" | "Signature"
 * - value: string (text or file URL)
 * - x, y, width, height: number
 * - page: number
 */
export function convertBackendToFrontend(backendData) {
  return backendData
    .map((field) => {
      const base = {
        ...field,
        id: field?.elementId || field.signatureId || field?.textId || field.id,
        x: field.x,
        y: field.y,
        page: field.page,
        width: field.width,
        height: field.height,
      };

      if (field.signatureFileUrl) {
        return {
          ...base,
          type: "Signature",
          value: field.signatureFileUrl,
          signatureId: field?.signatureId,
        };
      }

      if (field.text) {
        return {
          ...base,
          type: "Text",
          value: field.text,
          textId: field?.textId,
        };
      }

      return null;
    })
    .filter(Boolean);
}

const loadedFonts = new Set();
/**
 * load a font from google's font family
 */
export const loadFont = (fontName) => {
  if (loadedFonts.has(fontName)) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    if (document.fonts.check(`1em "${fontName}"`)) {
      loadedFonts.add(fontName);
      return resolve();
    }

    const link = document.createElement("link");
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
      fontName,
    )}&display=swap`;
    link.rel = "stylesheet";

    link.onload = () => {
      const checkFont = () => {
        if (document.fonts.check(`1em "${fontName}"`)) {
          loadedFonts.add(fontName);
          resolve();
        } else {
          setTimeout(checkFont, 100);
        }
      };
      checkFont();
    };

    link.onerror = () => {
      console.error(`Failed to load font: ${fontName}`);
      reject(new Error(`Font ${fontName} failed to load`));
    };

    document.head.appendChild(link);
  });
};

/**
 * Formats a Google Places API address object into a single-line address string.
 *
 * Extracts only the following components:
 * - premise
 * - subpremise
 * - street number + route
 * - city (locality)
 * - state (administrative_area_level_1)
 * - postal code
 *
 * @param {Object} address - The address object from Google Places API.
 * @param {Array} address.address_components - Array of address parts.
 * @returns {string} A single-line formatted address with relevant parts, separated by commas.
 *
 * @example
 * const formatted = formatAddress(place);
 * console.log(formatted); // "Building A, Apt 4B, 123 Main St, Toronto, ON, M4B 1B3"
 */
export const formatAddress = (address) => {
  let street_number = "",
    route = "",
    subpremise = "",
    premise = "",
    city = "",
    state = "",
    zipCode = "";

  for (let component of address?.address_components || []) {
    const types = component.types;

    if (types.includes("street_number")) {
      street_number = component.long_name;
    } else if (types.includes("route")) {
      route = component.long_name;
    } else if (types.includes("subpremise")) {
      subpremise = component.long_name;
    } else if (types.includes("premise")) {
      premise = component.long_name;
    } else if (types.includes("locality")) {
      city = component.long_name;
    } else if (types.includes("administrative_area_level_1")) {
      state = component.short_name || component.long_name;
    } else if (types.includes("postal_code")) {
      zipCode = component.long_name;
    }
  }

  const parts = [
    premise,
    subpremise,
    street_number && route
      ? `${street_number} ${route}`
      : route || street_number,
    city,
    state,
    zipCode,
  ];

  // Filter out empty values and join with commas
  const formattedAddress = parts.filter(Boolean).join(", ");

  return formattedAddress;
};

/**
 * check if a contractor is third party
 */
export const isThirdPartyContractor = ({ row }) => {
  return (
    row?.contractorType == CONTRACTOR_TYPE_3RD_PARTY.OUTSOURCED ||
    row?.contractorType == CONTRACTOR_TYPE_3RD_PARTY.THIRD_PARTY
  );
};

/**
 * check if it's outsourced contractor
 */
export const isOutSorucedContractor = ({ row }) => {
  return row?.contractorType == CONTRACTOR_TYPE_3RD_PARTY.OUTSOURCED;
};

/**
 * Compares two objects for deep equality
 * @param {Object} params
 * @param {Object} params.obj1 - First object to compare
 * @param {Object} params.obj2 - Second object to compare
 * @returns {boolean} True if objects are deeply equal
 */
export const compareObjects = ({ obj1, obj2 }) => {
  return _.isEqual(obj1, obj2);
};

/**
 * Compares values at specified keys (supports nested paths with dot notation)
 * @param {Object} params
 * @param {Object} params.obj1 - First object (or only object if comparing within same object)
 * @param {Object} [params.obj2] - Second object (optional, for cross-object comparison)
 * @param {string} params.key1 - First key path (e.g., 'user.name')
 * @param {string} [params.key2] - Second key path (optional, for same-object comparison)
 * @returns {boolean} True if values at keys are equal
 */
export const compareKeys = ({ obj1, obj2, key1, key2 }) => {
  if (obj2 && !key2) {
    // Compare same key across two objects
    return _.isEqual(_.get(obj1, key1), _.get(obj2, key1));
  }

  if (key2 && !obj2) {
    // Compare two keys within same object
    return _.isEqual(_.get(obj1, key1), _.get(obj1, key2));
  }

  return false;
};

/**
 * Detects media type from a URL (e.g., S3 URL) based on file extension.
 *
 * @param {string} url - The file URL (can include query params).
 * @returns {"image" | "video" | "audio" | "pdf" | "document" | "archive" | "unknown"}
 */
export function getMediaTypeFromUrl(url) {
  if (!url || typeof url !== "string") return "unknown";

  // Remove query params & fragments
  const cleanUrl = url.split("?")[0].split("#")[0];

  // Extract extension
  const extension = cleanUrl.split(".").pop()?.toLowerCase();

  if (!extension) return "unknown";

  const IMAGE_EXTENSIONS = [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "webp",
    "svg",
    "bmp",
    "tiff",
  ];
  const VIDEO_EXTENSIONS = ["mp4", "webm", "mov", "avi", "mkv", "m4v"];
  const AUDIO_EXTENSIONS = ["mp3", "wav", "ogg", "aac", "flac"];
  const DOCUMENT_EXTENSIONS = [
    "doc",
    "docx",
    "xls",
    "xlsx",
    "ppt",
    "pptx",
    "txt",
    "csv",
  ];
  const ARCHIVE_EXTENSIONS = ["zip", "rar", "7z", "tar", "gz"];

  if (IMAGE_EXTENSIONS.includes(extension)) return "image";
  if (VIDEO_EXTENSIONS.includes(extension)) return "video";
  if (AUDIO_EXTENSIONS.includes(extension)) return "audio";
  if (extension === "pdf") return "pdf";
  if (DOCUMENT_EXTENSIONS.includes(extension)) return "document";
  if (ARCHIVE_EXTENSIONS.includes(extension)) return "archive";

  return "unknown";
}